import { useState, useEffect } from 'react';
import { Search, Users, UserCheck, Building } from 'lucide-react';
import type { ReferrerRef, RefPartner, Staff, Stakeholder } from '../types';
import { partnerService } from '../api/partners';
import { staffService } from '../api/staff';
import { stakeholderService } from '../api/stakeholders';
import { referralService } from '../api/referrals';

interface SourcePickerProps {
  value: ReferrerRef | null;
  onChange: (referrer: ReferrerRef) => void;
}

export default function SourcePicker({ value, onChange }: SourcePickerProps) {
  const [activeTab, setActiveTab] = useState<'partners' | 'staff' | 'stakeholders' | 'code'>('partners');
  const [searchQuery, setSearchQuery] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [partners, setPartners] = useState<RefPartner[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [partnersData, staffData, stakeholdersData] = await Promise.all([
        partnerService.getAll(),
        staffService.getAll(),
        stakeholderService.getAll(),
      ]);
      setPartners(partnersData.filter(p => p.active));
      setStaff(staffData.filter(s => s.active));
      setStakeholders(stakeholdersData.filter(s => s.active));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeResolve = async () => {
    if (!codeInput.trim()) return;

    try {
      setLoading(true);
      setError('');
      const resolved = await referralService.resolveReferrerByCode(codeInput.trim());
      
      if (resolved) {
        onChange({
          kind: resolved.kind as any,
          id: resolved.entity.id,
          code: codeInput.trim(),
        });
        setCodeInput('');
      } else {
        setError('Invalid referral code');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (kind: ReferrerRef['kind'], entity: RefPartner | Staff | Stakeholder) => {
    onChange({
      kind,
      id: entity.id,
      code: 'referralCode' in entity ? entity.referralCode : undefined,
    });
  };

  const filterItems = (items: any[], query: string) => {
    if (!query) return items;
    const lowerQuery = query.toLowerCase();
    return items.filter(item => 
      item.name.toLowerCase().includes(lowerQuery) ||
      ('empCode' in item && item.empCode.toLowerCase().includes(lowerQuery)) ||
      ('role' in item && item.role.toLowerCase().includes(lowerQuery))
    );
  };

  const tabs = [
    { id: 'partners', label: 'Partners', icon: Building, count: partners.length },
    { id: 'staff', label: 'Staff', icon: UserCheck, count: staff.length },
    { id: 'stakeholders', label: 'Stakeholders', icon: Users, count: stakeholders.length },
    { id: 'code', label: 'Code', icon: Search, count: 0 },
  ];

  const renderSelectedValue = () => {
    if (!value) return 'Select referrer...';
    
    const entity = 
      partners.find(p => p.id === value.id) ||
      staff.find(s => s.id === value.id) ||
      stakeholders.find(s => s.id === value.id);
    
    if (entity) {
      const kindLabel = value.kind === 'external_partner' ? 'Partner' : 
                       value.kind === 'internal_staff' ? 'Staff' : 'Stakeholder';
      return `${entity.name} (${kindLabel})`;
    }
    
    return value.code || 'Unknown referrer';
  };

  if (loading && partners.length === 0) {
    return (
      <div className="border border-gray-300 rounded-md p-4">
        <div className="animate-pulse">Loading referrers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-700">
        Selected: {renderSelectedValue()}
      </div>

      <div className="border border-gray-300 rounded-lg">
        {/* Tab Headers */}
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span className="bg-gray-200 text-gray-600 px-2 py-1 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'code' ? (
            <div className="space-y-3">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  placeholder="Enter referral code (e.g., RP-8G3KQ4)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleCodeResolve}
                  disabled={!codeInput.trim() || loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Resolve
                </button>
              </div>
              {error && (
                <div className="text-sm text-red-600">{error}</div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${activeTab}...`}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* List */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                {activeTab === 'partners' && 
                  filterItems(partners, searchQuery).map((partner) => (
                    <div
                      key={partner.id}
                      onClick={() => handleSelect('external_partner', partner)}
                      className={`p-3 border rounded-md cursor-pointer transition-colors ${
                        value?.id === partner.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">{partner.name}</div>
                      <div className="text-sm text-gray-500">
                        {partner.role} • {partner.referralCode}
                      </div>
                    </div>
                  ))
                }

                {activeTab === 'staff' && 
                  filterItems(staff, searchQuery).map((member) => (
                    <div
                      key={member.id}
                      onClick={() => handleSelect('internal_staff', member)}
                      className={`p-3 border rounded-md cursor-pointer transition-colors ${
                        value?.id === member.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">{member.name}</div>
                      <div className="text-sm text-gray-500">
                        {member.empCode} • {member.role} • {member.referralCode}
                      </div>
                    </div>
                  ))
                }

                {activeTab === 'stakeholders' && 
                  filterItems(stakeholders, searchQuery).map((stakeholder) => (
                    <div
                      key={stakeholder.id}
                      onClick={() => handleSelect('stakeholder', stakeholder)}
                      className={`p-3 border rounded-md cursor-pointer transition-colors ${
                        value?.id === stakeholder.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">{stakeholder.name}</div>
                      <div className="text-sm text-gray-500">
                        {stakeholder.type} • {stakeholder.referralCode}
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
